import React from "react";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { PERMISSION_GROUPS } from "./permissionCatalogue";
import { ChamberStaffUserViewModel } from "./types";
import { applyCmpOrganizationNoun } from "../../../../../utils/cmpOrganizationNoun";
import { useCmpOrganizationNoun } from "../../../../../contexts/CmpOrganizationNounContext";

export type EmployeePermissionGroupView = {
  title: string;
  items: string[];
};

// Only the groups with at least one assigned permission, in catalogue order.
export const getAssignedPermissionGroups = (
  user: Pick<ChamberStaffUserViewModel, "permissions">
): EmployeePermissionGroupView[] =>
  PERMISSION_GROUPS.map((group) => ({
    title: group.label,
    items: group.permissions
      .filter((permission) =>
        user.permissions[group.platform].includes(permission.value)
      )
      .map((permission) => permission.label),
  })).filter((group) => group.items.length > 0);

const CheckGlyph = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#10B981"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export type EmployeePermissionsDialogProps = {
  user: ChamberStaffUserViewModel | null;
  onClose: () => void;
};

// CMP Dashboard (24) permView: 560px card, title "<name> — Permissions",
// "<n> permissions assigned · <title>", groups "<title> (<n>)" with green
// ticks. View only: it has no actions.
export const EmployeePermissionsDialog = ({
  user,
  onClose,
}: EmployeePermissionsDialogProps) => {
  const noun = useCmpOrganizationNoun();
  const groups = user ? getAssignedPermissionGroups(user) : [];
  const total = groups.reduce((count, group) => count + group.items.length, 0);
  const name = user ? `${user.firstName} ${user.lastName}` : "";

  return (
    <Dialog
      open={Boolean(user)}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby="employee-permissions-dialog-title"
      sx={{
        // Same layer as the other design pop-ups (DesignModalShell).
        zIndex: 10000,
        "& .MuiDialog-container": { boxSizing: "border-box", padding: "20px" },
      }}
      BackdropProps={{ sx: { backgroundColor: "rgba(10, 10, 10, 0.45)" } }}
      PaperProps={{
        sx: {
          backgroundColor: "#FFFFFF",
          border: "1px solid #E5E7EB",
          borderRadius: "14px",
          boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          fontFamily: "'DM Sans', sans-serif",
          margin: 0,
          maxHeight: "90vh",
          // Replaces MUI's calc(100% - 64px), which made the card 286 px at
          // 390 (design: min(560px, 92vw) inside the 20 px overlay padding).
          maxWidth: "100%",
          width: "min(560px, 92vw)",
        },
      }}
    >
      <div className="flex items-start justify-between gap-[16px] border-b border-[#ECECEC] px-[28px] pb-[16px] pt-[24px]">
        <div>
          <div
            id="employee-permissions-dialog-title"
            className="text-[16px] font-semibold text-[#0A0A0A]"
          >
            {`${name} — Permissions`}
          </div>
          <div className="mt-[4px] text-[14px] text-[#0A0A0A]">
            {`${total} ${total === 1 ? "permission" : "permissions"} assigned${
              user?.jobTitle ? ` · ${user.jobTitle}` : ""
            }`}
          </div>
        </div>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{
            backgroundColor: "#F21B1B",
            color: "#FFFFFF",
            flexShrink: 0,
            height: 28,
            padding: 0,
            width: 28,
            "&:hover": { backgroundColor: "#D01414" },
          }}
        >
          <CloseIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </div>
      <div className="flex min-h-0 flex-col gap-[18px] overflow-y-auto px-[28px] pb-[24px] pt-[20px]">
        {groups.length === 0 ? (
          <div className="text-[14px] text-[#727272]">
            No permissions have been assigned to this employee.
          </div>
        ) : (
          groups.map((group) => (
            <div key={group.title} className="flex flex-col gap-[8px]">
              <div className="text-[14px] font-semibold text-[#0A0A0A]">
                {applyCmpOrganizationNoun(group.title, noun)}{" "}
                <span className="font-normal text-[#727272]">
                  ({group.items.length})
                </span>
              </div>
              <div className="flex flex-col gap-[6px]">
                {group.items.map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-[10px] text-[14px] text-[#0A0A0A]"
                  >
                    <CheckGlyph />
                    <span>{applyCmpOrganizationNoun(item, noun)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </Dialog>
  );
};

export default EmployeePermissionsDialog;
